import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Loader2, LogOut, Trash2, Phone, Mail, RefreshCw } from "lucide-react";
import { adminLogin, fetchLeads, updateLeadStatus, deleteLead } from "@/lib/api";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS = ["new", "contacted", "won", "lost"];
const statusColor = { new: "bg-blue-100 text-blue-700", contacted: "bg-amber-100 text-amber-700", won: "bg-emerald-100 text-emerald-700", lost: "bg-slate-200 text-slate-600" };

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem("jr_admin_token") || "");
  const [password, setPassword] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(false);

  const load = useCallback(async (t) => {
    setLoading(true);
    try {
      const data = await fetchLeads(t);
      setLeads(data);
      setAuthed(true);
    } catch { toast.error("Session expired. Please login again."); setAuthed(false); localStorage.removeItem("jr_admin_token"); setToken(""); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (token) load(token); }, [token, load]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { token: t } = await adminLogin(password);
      localStorage.setItem("jr_admin_token", t);
      setToken(t);
    } catch { toast.error("Invalid password"); } finally { setLoading(false); }
  };

  const logout = () => { localStorage.removeItem("jr_admin_token"); setToken(""); setAuthed(false); };

  const changeStatus = async (id, status) => {
    await updateLeadStatus(id, status, token);
    setLeads((l) => l.map((x) => (x.id === id ? { ...x, status } : x)));
    toast.success("Status updated");
  };
  const remove = async (id) => {
    await deleteLead(id, token);
    setLeads((l) => l.filter((x) => x.id !== id));
    toast.success("Lead deleted");
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-navy-deep flex items-center justify-center px-5" data-testid="admin-login">
        <form onSubmit={login} className="glass-dark rounded-lg p-8 w-full max-w-sm text-white">
          <div className="flex items-center gap-2 text-sky mb-4"><ShieldCheck className="h-6 w-6" /><span className="font-display font-extrabold text-lg">JAL RAKSHAKK Admin</span></div>
          <p className="text-slate-300 text-sm mb-5">Enter the admin password to view leads.</p>
          <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" data-testid="admin-password" className="w-full bg-white/5 border border-white/15 rounded-sm px-3 py-2.5 text-white placeholder:text-slate-500 focus:outline-none focus:border-sky mb-4" />
          <button disabled={loading} className="btn-ripple w-full bg-royal hover:bg-royal/90 text-white font-semibold py-3 rounded-sm transition-colors flex items-center justify-center gap-2" data-testid="admin-login-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Login
          </button>
          <Link to="/" className="block text-center text-slate-400 text-xs mt-4 hover:text-sky">← Back to website</Link>
        </form>
      </div>
    );
  }

  const counts = { total: leads.length, new: leads.filter((l) => l.status === "new").length, won: leads.filter((l) => l.status === "won").length };

  return (
    <div className="min-h-screen bg-soft-grey" data-testid="admin-dashboard">
      <header className="bg-navy-deep text-white">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2"><ShieldCheck className="h-6 w-6 text-sky" /><span className="font-display font-extrabold">Leads Dashboard</span></div>
          <div className="flex items-center gap-3">
            <button onClick={() => load(token)} className="text-slate-300 hover:text-white flex items-center gap-1 text-sm"><RefreshCw className="h-4 w-4" /> Refresh</button>
            <button onClick={logout} className="text-slate-300 hover:text-white flex items-center gap-1 text-sm" data-testid="admin-logout"><LogOut className="h-4 w-4" /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-8">
        <div className="grid grid-cols-3 gap-4 mb-8">
          {[{ l: "Total Leads", v: counts.total }, { l: "New", v: counts.new }, { l: "Won", v: counts.won }].map((s) => (
            <div key={s.l} className="bg-white rounded-lg p-5 border border-slate-200">
              <div className="text-xs uppercase tracking-wider text-slate-500">{s.l}</div>
              <div className="font-display text-3xl font-extrabold text-navy-deep">{s.v}</div>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : leads.length === 0 ? (
          <div className="text-center py-20 text-slate-500">No leads yet.</div>
        ) : (
          <div className="bg-white rounded-lg border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-soft-grey text-slate-500 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left p-4">Contact</th>
                  <th className="text-left p-4">Service / Area</th>
                  <th className="text-left p-4">Source</th>
                  <th className="text-left p-4">Message</th>
                  <th className="text-left p-4">Status</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {leads.map((l) => (
                  <tr key={l.id} className="border-t border-slate-100" data-testid={`lead-row-${l.id}`}>
                    <td className="p-4">
                      <div className="font-semibold text-navy-deep">{l.name}</div>
                      <div className="flex items-center gap-2 text-slate-500 text-xs mt-1">
                        <a href={`tel:${l.phone}`} className="flex items-center gap-1 hover:text-royal"><Phone className="h-3 w-3" />{l.phone}</a>
                        {l.email && <a href={`mailto:${l.email}`} className="flex items-center gap-1 hover:text-royal"><Mail className="h-3 w-3" /></a>}
                      </div>
                    </td>
                    <td className="p-4 text-slate-600">{l.service || "—"}<div className="text-xs text-slate-400">{l.area || ""}</div></td>
                    <td className="p-4"><span className="text-xs bg-slate-100 px-2 py-1 rounded">{l.source}</span></td>
                    <td className="p-4 text-slate-600 max-w-[220px] truncate">{l.message || "—"}</td>
                    <td className="p-4">
                      <Select value={l.status} onValueChange={(v) => changeStatus(l.id, v)}>
                        <SelectTrigger className={`w-32 h-8 text-xs ${statusColor[l.status]}`} data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="p-4"><button onClick={() => remove(l.id)} className="text-slate-400 hover:text-red-500" data-testid={`lead-delete-${l.id}`}><Trash2 className="h-4 w-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
